import React, { useState, useEffect } from 'react';
import { expertDashboardApi } from '../../../lib/http';

interface Service {
  id: string;
  name: string;
  duration_min: number;
  price_cents: number;
  currency: string;
  description?: string;
}

interface ServiceSelectorProps {
  selectedServiceId: string | null;
  selectedServiceName: string;
  error?: string;
  onServiceSelect: (service: Service) => void;
  startTime?: string | null;
  workingHoursEnd?: string | null;
}

const timeToMinutes = (time: string): number => {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + (m || 0);
};

const ServiceSelector: React.FC<ServiceSelectorProps> = ({
  selectedServiceId,
  selectedServiceName,
  error,
  onServiceSelect,
  startTime,
  workingHoursEnd,
}) => {
  const [services, setServices] = useState<Service[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchFilter, setSearchFilter] = useState('');

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    setIsLoading(true);
    try {
      const response = await expertDashboardApi.services.list({ active_only: true });
      setServices(response.data.services || []);
    } catch (error) {
      console.error('Failed to load services:', error);
      setServices([]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatPrice = (cents: number, currency: string = 'USD'): string => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
  };

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
    return `${hours}h ${mins}min`;
  };

  const filteredServices = services.filter((service) =>
    service.name.toLowerCase().includes(searchFilter.toLowerCase())
  );

  const canCheckFit = !!(startTime && workingHoursEnd);
  const remainingMinutes = canCheckFit
    ? timeToMinutes(workingHoursEnd!) - timeToMinutes(startTime!)
    : null;

  const serviceFitsInSlot = (service: Service): boolean => {
    if (!canCheckFit || remainingMinutes === null) return true;
    return service.duration_min <= remainingMinutes;
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-3">
        Service <span className="text-red-500">*</span>
      </label>

      {isLoading ? (
        <div className="text-center py-8">
          <svg className="animate-spin h-8 w-8 text-purple-600 mx-auto" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className="text-sm text-gray-600 mt-2">Loading services...</p>
        </div>
      ) : services.length === 0 ? (
        <div className="text-center py-8 px-4 bg-gray-50 rounded-lg border border-gray-200">
          <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <p className="text-base font-medium text-gray-900 mb-2">No services available</p>
          <p className="text-sm text-gray-600 mb-4">
            Add services in your profile to start booking
          </p>
        </div>
      ) : (
        <>
          {/* Search filter if > 6 services */}
          {services.length > 6 && (
            <div className="mb-3">
              <input
                type="text"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                placeholder="Filter services..."
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
              {searchFilter && (
                <p className="mt-1 text-xs text-gray-500">
                  {filteredServices.length} of {services.length} services
                </p>
              )}
            </div>
          )}

          {/* Service list */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredServices.length === 0 ? (
              <p className="text-center py-4 text-sm text-gray-500">
                No services match your filter
              </p>
            ) : (
              filteredServices.map((service) => {
                const isSelected = selectedServiceId === service.id;
                const fits = serviceFitsInSlot(service);

                return (
                  <label
                    key={service.id}
                    className={`flex items-start gap-3 p-3 border-2 rounded-xl transition-all ${
                      !fits
                        ? 'bg-gray-50 border-gray-200 opacity-50 cursor-not-allowed'
                        : isSelected
                        ? 'bg-purple-50 border-purple-600 cursor-pointer'
                        : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300 cursor-pointer'
                    }`}
                  >
                    <input
                      type="radio"
                      name="service"
                      value={service.id}
                      checked={isSelected}
                      disabled={!fits}
                      onChange={() => fits && onServiceSelect(service)}
                      className="mt-1 w-5 h-5 text-purple-600 focus:ring-purple-500 flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className={`font-semibold ${fits ? 'text-gray-900' : 'text-gray-400'}`}>{service.name}</div>
                      <div className={`text-sm mt-0.5 ${fits ? 'text-gray-600' : 'text-gray-400'}`}>
                        {formatDuration(service.duration_min)} • {formatPrice(service.price_cents, service.currency)}
                      </div>
                      {!fits && (
                        <div className="text-xs text-amber-600 mt-1">
                          Doesn't fit — exceeds working hours
                        </div>
                      )}
                      {fits && service.description && (
                        <div className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {service.description}
                        </div>
                      )}
                    </div>
                  </label>
                );
              })
            )}
          </div>
        </>
      )}

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default ServiceSelector;
